import React, { FC, useEffect, useState } from 'react';

import { Categories } from '@ariksa/inventory-core';
import {
  AppSchemasInventoryResourceCountDeploymentSummary,
  Resources,
} from '@ariksa/inventory-core/api';
import { Box, Stack } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';

import { WithResourceIcon } from 'components/DataDisplay';
import { CustomTable2 as Table } from 'components/DataDisplay/NewTable/Table';
import { useAccessBoundary } from 'containers/App/hooks/useAccessBoundary';
import { useResourceType } from 'containers/App/hooks/useResourceType';
import { useSearchParamFunctions } from 'containers/App/hooks/useSearchParamFunctions';
import { useCategoryTableColumns } from 'containers/Inventory/CloudInventory/Components/hooks/useCategoryTableColumns';
import { InstanceInventory } from 'containers/Inventory/CloudInventory/Components/InstanceInventory';
import { useInventoryContext } from 'containers/Inventory/CloudInventory/hooks/context';
import { useInventory } from 'containers/Inventory/CloudInventory/hooks/useInventory';

import { selectInventory } from '../../selector';
import { actions } from '../../slice';
import { InventoryTableTitle } from '../InventoryTableTitle';

export const ResourceTypeInventory: FC = () => {
  const dispatch = useDispatch();
  const {
    currentCategory,
    deploymentSummary,
    instanceResourceType,
  } = useSelector(selectInventory);

  const { withAccessBoundary } = useAccessBoundary();
  const { cloudNativeMapping, getCloudAgnosticName } = useResourceType();
  const { updateValueInURL } = useSearchParamFunctions();

  const [data, setData] = useState<
    AppSchemasInventoryResourceCountDeploymentSummary[]
  >([]);
  const { filterDeploymentSummary } = useInventory();
  const { useInventoryV3 } = useInventoryContext();
  const {
    deployedInAccounts,
    entityCount,
    cloud,
    deploymentType,
  } = useCategoryTableColumns();

  //get resource type inventory
  useEffect(() => {
    const type = currentCategory.split('_');
    const params = withAccessBoundary({
      category: type[0] as Categories,
      resourceType: type[1] as Resources,
      useInventoryV3,
    });
    dispatch(actions.getDeploymentSummary({ q: params } as any));
  }, [dispatch, currentCategory, withAccessBoundary, useInventoryV3]);

  useEffect(() => {
    setData(filterDeploymentSummary());
  }, [filterDeploymentSummary]);

  const handleRowClick = row => {
    updateValueInURL('resource_type', row.native_resource);
    //dispatch(actions.setNativeResource(row.native_resource));
    //dispatch(actions.resetSearchItems());
  };

  const columns = [
    {
      header: <Box pl="thLeftPaddingWithIcon">Name</Box>,
      accessor: 'native_resource',
      align: 'left',
      render: ({ value, row }) => (
        <WithResourceIcon resourceType={value}>
          <Box>{row?.native_name || value}</Box>
        </WithResourceIcon>
      ),
    },
    {
      header: 'Entity Type',
      accessor: 'native_resource',
      align: 'left',
      render: ({ value }) =>
        cloudNativeMapping[value]?.cloud_agnostic_name || value,
    },
    cloud,
    deployedInAccounts,
    entityCount,
    deploymentType,
  ];

  return !!instanceResourceType ? (
    <InstanceInventory />
  ) : (
    <Stack spacing={4} h="full">
      <InventoryTableTitle
        title={getCloudAgnosticName(currentCategory?.split('_')[1])}
        iconType={currentCategory?.split('_')[1]}
        value={
          <Box>
            {deploymentSummary.isLoading ? 0 : data?.length}
            {data?.length > 1 ? ' Entities' : ' Entity'}
          </Box>
        }
      />
      <Table
        columns={columns}
        data={data}
        onRowClick={handleRowClick}
        isLoading={deploymentSummary.isLoading}
      />
      {/* Multi allow-list modal removed: nothing could open it (its header button was commented out). */}
    </Stack>
  );
};
